import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Field, FieldArray, Formik, getIn} from "formik";
import PurchaseInfo from "../../Common/CartInfo";
import {useHistory, useParams} from "react-router";
import * as Yup from "yup";
import {useDispatch, useSelector} from "react-redux";
import {
    getAllParkers,
    getParkerData,
    hasReachedCheckout,
    setParker
} from "../../../../Store/purchase.slice";
import {TextField} from 'formik-material-ui';
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import CloseIcon from '@material-ui/icons/Close';
import IconButton from "@material-ui/core/IconButton";
import { fetchRateAccessDevices, rateById } from "../../../../Store/rates.slice";
import {nanoid} from "@reduxjs/toolkit";
import {get, isArray, isEmpty, map} from 'lodash';
import ListItemIcon from "@material-ui/core/ListItemIcon";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Alert from "@material-ui/lab/Alert";
import {states} from "../../../Common/USStates";
import {Autocomplete} from 'formik-material-ui-lab';
import MuiTextField from '@material-ui/core/TextField'
import accessDeviceTypes from "../../Common/accessDeviceTypes";
import {toDollars} from "../../utils";
import BackButton from "../../Common/BackButton";

const useStyles = makeStyles(theme => ({
    formContent: {
        padding: theme.spacing(3)
    },
    accessDeviceError: {width: '100%'},
    accessDeviceList: {
        maxWidth: 600,
        marginTop: theme.spacing(1)
    },
    accessDeviceType: {
        margin: 0,
        padding: 0,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-start",
        borderTop: '1px solid black',
        borderLeft: '1px solid black',
        borderRight: '1px solid black',
        '&:first-child': {
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px'
        },
        '&:last-child': {
            borderBottom: '1px solid black',
            borderBottomLeftRadius: '3px',
            borderBottomRightRadius: '3px'
        },
        '&:hover, &.Mui-selected': {
            border: `1px solid ${theme.palette.primary.main}`,
            '& + $accessDeviceType': {borderTop: 0},
            '& > $accessDeviceIcon': {
                backgroundColor: theme.palette.primary.main,
                '& > .MuiSvgIcon-root': {color: 'white'},
            }
        }
    },
    accessDeviceIcon: {
        width: '20%',
        minWidth: 'fit-content',
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(1),
    },
    accessDeviceText: {
        margin: 0,
        width: '80%',
        overflowWrap: "break-word",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    }
}))

// If isDataCy is true, convert the text to a slug, otherwise use human readable text
const getVehicleText = (vehicles, isDataCy) => {
    let vehicleText = !vehicles.length ? 'Add Vehicle' : 'Add Another Vehicle';

    if(isDataCy){
        vehicleText = vehicleText.toLowerCase().split(' ').join('-');
    }

    return vehicleText
}

export function ParkerPage() {
    const classes = useStyles();
    const history = useHistory()
    const dispatch = useDispatch();
    const {parkerId} = useParams();
    const allParkers = useSelector(getAllParkers);
    const reachedCheckout = useSelector(hasReachedCheckout)
    const parkerData = useMemo(() => getParkerData(allParkers, parkerId), [allParkers, parkerId]);
    const [rateAccessDevices, setRateAccessDevices] = useState([]);

    const requiredAccessDevices = rateAccessDevices.filter(({ required }) => required);
    const requiredAccessDeviceIds = (requiredAccessDevices ?? []).map(({ id }) => id);

    const accessDeviceIds = rateAccessDevices.map(({ id }) => id);

    const rateData = useSelector(state => rateById(state, parkerData.rateId));
    const minVehicleCount = (rateData && ['OPTIONAL', 'DO_NOT_DISPLAY'].includes(rateData.require_car_info)) ? 0 : 1;
    const initialVehicles = minVehicleCount === 0 ? [] : [{license: '', state: '', make: '', model: '', color: '', decal: ''}];
    const initialRedirect = !!reachedCheckout ? '/checkout' : '/survey';

    const init = {
        first_name: get(parkerData, 'first_name', ''),
        last_name: get(parkerData, 'last_name', ''),
        email: get(parkerData, 'email', ''),
        phone: get(parkerData, 'phone', ''),
        address: get(parkerData, 'address', ''),
        city: get(parkerData, 'city', ''),
        state: get(parkerData, 'state', ''),
        zip: get(parkerData, 'zip', ''),
        requireCarInfo: get(rateData, 'require_car_info', 'OPTIONAL'),
        vehicles: get(parkerData, 'vehicles', initialVehicles),
        accessDevices: requiredAccessDeviceIds,
        redirect: initialRedirect
    };

    const schema = Yup.object({
        first_name: Yup.string().required('First Name is required.'),
        last_name: Yup.string().required('Last Name is required.'),
        email: Yup.string().email().required('Email is required.'),
        phone: Yup.string().matches(
            /^[(\-.\s]?([0-9]{3})[)\-.\s]?\s?([0-9]{3})[(\-.\s]?([0-9]{4})$/,
            "Phone number must match 999-999-9999."
        ).required('Phone Number is required.'),
        address: Yup.string().required('Address is required.'),
        city: Yup.string().required('City is required.'),
        state: Yup.string().required("State is required."),
        zip: Yup.string().min(5).required('Zip Code is required.'),
        requireCarInfo: Yup.string(),
        vehicles: Yup.array().of(Yup.object({
            license: Yup.string().when('requireCarInfo', (requireCarInfo, schema) => {
                return requireCarInfo === 'REQUIRED' ? schema.required("License is required.") : schema.notRequired();
            }),
            state: Yup.string().when('requireCarInfo', (requireCarInfo, schema) => {
                return requireCarInfo === 'REQUIRED' ? schema.required("State is required.") : schema.notRequired();
            }),
            make: Yup.string(),
            model: Yup.string(),
            color: Yup.string(),
            decal: Yup.string()
        })).min(minVehicleCount).max(2),
        accessDevices: Yup.array(),
        accessDevicesValid: Yup.array().when(['accessDevices'], {
            is: (requiredAccessDevicesFromApi) => {
              return requiredAccessDevicesFromApi.length > 0;
            },
          then: Yup.array().of(Yup.mixed().oneOf(accessDeviceIds).required()).min(1),
        }),
    });

    useEffect(() => {
        (async () => {
            const { payload } = await dispatch(fetchRateAccessDevices({
                rateId: parkerData.rateId,
                parkerStartDate: parkerData.startDate,
            }));
            setRateAccessDevices(payload);
        })();
    }, [dispatch, parkerData]);

    const onSubmit = useCallback(async ({redirect, ...values}) => {
        await dispatch(setParker({id: parkerId, ...values}))
        history.push(redirect);
    }, [dispatch, history, parkerId]);

    useEffect(() => {
        if (isEmpty(rateData)) history.push('/');
    }, [history, rateData]);

    return <Formik initialValues={init} onSubmit={onSubmit} validationSchema={schema} enableReinitialize>
        {({values, setFieldValue, submitForm, touched, errors, dirty}) => <Grid container>
            <Grid item xs={12}>
                <LinearProgress variant="determinate" value={20}/>
            </Grid>
            <Grid className={classes.formContent} item xs={12} md={7}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant='h5'>Provide the Parker's Information</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Field component={TextField} name="first_name" placeholder="Jane" label="First Name" fullWidth inputProps={{"data-cy": "first-name-input"}}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Field component={TextField} name="last_name" placeholder="Smith" label="Last Name" fullWidth inputProps={{"data-cy": "last-name-input"}}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Field component={TextField} name="email" placeholder="jane@example.com" label="Email"
                               fullWidth inputProps={{"data-cy": "email-input"}}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Field component={TextField} name="phone" placeholder="123-456-7890" label="Phone Number"
                               fullWidth inputProps={{"data-cy": "phone-number-input"}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Field component={TextField} name="address" placeholder="123 Main Street" label="Street Address"
                               fullWidth inputProps={{"data-cy": "address-input"}}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Field component={TextField} name="city" placeholder="Chicago" label="City" fullWidth inputProps={{"data-cy": "city-input"}}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Field name="state" component={Autocomplete}
                               options={states.map(s => s === 'Select One' ? '' : s)}
                               getOptionLabel={(option) => option} renderInput={(p) => (
                            <MuiTextField label="State" placeholder="Illinois" {...p}
                                          error={touched['state'] && !!errors['state']}
                                          helperText={touched['state'] && !!errors['state'] ? errors['state'] : p.helperText}
                                          fullWidth data-cy="state-text-field"/>
                        )}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Field component={TextField} name="zip" placeholder="12345" label="Zip Code" fullWidth inputProps={{"data-cy": "zip-code-input"}}/>
                    </Grid>
                    {(rateData && ['REQUIRED', 'OPTIONAL'].includes(rateData.require_car_info)) &&
                    <>
                        <Grid item xs={12}>
                            <Typography variant='h5'>Vehicle Information</Typography>
                        </Grid>
                        <FieldArray name="vehicles">
                            {({remove, push}) => (<Grid item container>
                                {values['vehicles'].map((v, index) => (<Grid spacing={3} item container key={index}>
                                    <Grid item xs={12}>
                                        {((rateData.require_car_info === 'REQUIRED' && index > 0) || rateData.require_car_info === 'OPTIONAL') ?
                                            <Box mt={2} display="flex" justifyContent="flex-end">
                                                <IconButton size="small" variant="contained" color="secondary"
                                                            onClick={() => remove(index)} data-cy={`vehicle-information-close-button-${index}`}><CloseIcon/></IconButton></Box> : ''}
                                        <Field component={TextField} name={`vehicles.${index}.license`}
                                               placeholder="ABC-1234"
                                               label="License Plate Number" fullWidth inputProps={{"data-cy": `vehicle-information-license-plate-number-input-${index}`}}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field name={`vehicles.${index}.state`} component={Autocomplete}
                                               options={states.map(s => s === 'Select One' ? '' : s)}
                                               getOptionLabel={(option) => option} renderInput={(p) => {
                                            const fieldError = getIn(errors, `vehicles.${index}.state`);
                                            const showError = getIn(touched, `vehicles.${index}.state`) && !!fieldError;
                                            return <MuiTextField label="State" placeholder="Illinois" {...p}
                                                                 error={showError}
                                                                 helperText={showError ? fieldError : p.helperText}
                                                                 fullWidth data-cy={`vehicle-information-state-text-field-${index}`}/>
                                        }} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field component={TextField} name={`vehicles.${index}.make`} label="Make"
                                               fullWidth inputProps={{"data-cy": `vehicle-information-make-input-${index}`}}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field component={TextField} name={`vehicles.${index}.model`} label="Model"
                                               fullWidth inputProps={{"data-cy": `vehicle-information-model-input-${index}`}}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field component={TextField} name={`vehicles.${index}.color`} label="Color"
                                               fullWidth inputProps={{"data-cy": `vehicle-information-color-input-${index}`}}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field component={TextField} name={`vehicles.${index}.decal`} label="Decal"
                                               fullWidth inputProps={{"data-cy": `vehicle-information-decal-input-${index}`}}/>
                                    </Grid>
                                </Grid>))}
                                {values['vehicles'].length <= 1 && <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end" m={values['vehicles'].length ? 3 : 0}>
                                        <Button onClick={() => push({license: '', state: '', make: '', model: '', color: '', decal: ''})}
                                                variant="contained" color="primary" disableElevation data-cy={`${getVehicleText(values['vehicles'], true)}-button`}>
                                            {getVehicleText(values['vehicles'], false)}
                                        </Button>
                                    </Box>
                                </Grid>}
                            </Grid>)}
                        </FieldArray>
                    </>
                    }

                    {/* only show that an access device is reqired if there are access devices to chose from */}
                    {errors.accessDevicesValid ? <Grid item xs={12}>
                        <Alert className={classes.accessDeviceError} severity='error' width={1} data-cy="access-devices-error-alert">
                            {isArray(errors.accessDevicesValid) ? map(errors.accessDevicesValid, (m, i) => 
                                <Typography key={i} variant="body1" data-cy={`access-devices-unexpected-error-text-${i}`}>{m}</Typography>) :
                                <Typography variant="body1" data-cy="access-devices-required-error-text">Access devices are required</Typography>}
                        </Alert>
                    </Grid> : null}
                    {!!rateAccessDevices.length && <Grid item xs={12}>
                        <Typography variant='h5'>
                            {'Select an Access Device'}
                        </Typography>
                        <Typography variant='body1'>{rateData.access_device_message}</Typography>
                        <List className={classes.accessDeviceList}>
                            {rateAccessDevices.map(({ id, type, required, price }) => (
                                <ListItem className={classes.accessDeviceType} key={id} button onClick={() => {
                                    if (required) return;
                                    const found = values.accessDevices.includes(id);
                                    if (found) {
                                        setFieldValue('accessDevices', values.accessDevices.filter(ad => ad !== id))
                                    } else {
                                        setFieldValue('accessDevices', values.accessDevices.concat([id]));
                                    }
                                }} selected={values.accessDevices.includes(id)} data-cy={`${values.accessDevices.includes(id) ? 'selected' : 'unselected'}-access-device-button`}>
                                    <ListItemIcon className={classes.accessDeviceIcon} data-cy="access-device-icon">
                                        {accessDeviceTypes[type].icon}
                                    </ListItemIcon>
                                    <ListItemText className={classes.accessDeviceText} data-cy="access-device-text">
                                        {`${accessDeviceTypes[type].text} - ${price ? toDollars(price) : 'Free'}`}
                                    </ListItemText>
                                </ListItem>))}
                        </List>
                    </Grid>}
                </Grid>
            </Grid>
            <Grid item xs={12} md={5}><Box m={3}>
                <PurchaseInfo
                    confirmOnRedirect={!!dirty}
                    currentParkerId={parkerId}
                    addedAccessDevices={values.accessDevices}
                />
            </Box></Grid>
            <Grid container item xs={12} justify="space-between">
                <Box m={3}><BackButton confirmOnRedirect={!!dirty}/></Box>
                <Box display="flex" flexWrap="wrap">
                    <Box m={3}>
                        <Button size="large" variant="contained" color="primary" onClick={async () => {
                            setFieldValue('redirect', `/rates/${nanoid()}`)
                            await submitForm()
                        }} data-cy="add-parker-button">Add New Parker</Button>
                    </Box>
                    <Box m={3}>
                        <Button size="large" variant="contained" color="primary" onClick={async () => {
                            setFieldValue('redirect', initialRedirect);
                            await submitForm();
                        }} data-cy="form-submit-button">Next</Button>
                    </Box>
                </Box>
            </Grid>
        </Grid>}
    </Formik>;
}

export default React.memo(ParkerPage);
